"use client";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ListChecks } from "lucide-react";

import PageContainer from "@/app/admin/_common/PageContainer";
import CuoButton from "@/app/admin/_common/CuoButton";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";

import { DETAILS, useChecklistEditorModel } from "./checklistEditorPageModel";
import DetailsPane from "./_cmp/DetailsPane";
import SectionCard from "./_cmp/SectionCard";
import TemplateSidebar from "./_cmp/TemplateSidebar";

export default function ChecklistEditorPage() {
  const navigate = useNavigate();
  const m = useChecklistEditorModel();

  return (
    <PageContainer flush>
      <div className="flex h-full w-full flex-col">
        <div className="flex items-center gap-2 border-b border-border bg-card px-3 py-3 min-h-[55px]">
          <button
            type="button"
            onClick={() => navigate("/admin/tools/checklists")}
            className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
            aria-label="Back to checklists"
          >
            <ArrowLeft className="size-4" />
          </button>
          {/* my-0: preflight is disabled, so <h1> keeps the UA margin otherwise. */}
          <h1 className="my-0 flex min-w-0 flex-1 items-center gap-2 truncate text-[21px] font-bold leading-none tracking-tight text-foreground">
            <ListChecks className="size-5 flex-none" />
            {m.details.name || (m.isNew ? "New checklist template" : "Checklist template")}
          </h1>
          {m.isDirty && (
            <>
              <CuoButton
                variant="outline"
                label="Discard"
                disabled={m.isSaving}
                onClick={m.discard}
              />
              <CuoButton label="Save" disableOnAsync={m.isSaving} onClick={m.save} />
            </>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {m.isLoading ? (
            <div className="flex w-full flex-none flex-col gap-2 border-b border-border bg-card p-4 md:w-64 md:border-b-0 md:border-r">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-5 w-full" />
              ))}
            </div>
          ) : (
            <TemplateSidebar
              categories={m.categories}
              selectedUid={m.selectedUid}
              detailsSelected={m.selectedUid === DETAILS}
              onSelectDetails={() => m.setSelectedUid(DETAILS)}
              onSelectSection={m.setSelectedUid}
              onAddCategory={m.addCategory}
              onAddSection={m.addSection}
              onRenameCategory={m.renameCategory}
              onSetCategoryIcon={m.setCategoryIcon}
              onRemoveCategory={m.removeCategory}
              onReorderCategory={m.reorderCategory}
              onRenameSection={m.renameSection}
              onRemoveSection={m.removeSection}
              onMoveSection={m.moveSection}
              highlightNonceFor={m.highlightNonceFor}
            />
          )}

          {/* Top padding lives on the inner column: padding on the scroller insets where sticky headers pin. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5">
            <div className="mx-auto flex w-full max-w-[920px] flex-col gap-4 pb-24 pt-5">
              {m.isLoading ? (
                <>
                  <Skeleton className="h-28 w-full" />
                  <Skeleton className="h-48 w-full" />
                </>
              ) : (
                <>
                  {m.selectedUid === DETAILS ? (
                    <DetailsPane
                      name={m.details.name}
                      description={m.details.description}
                      onChange={(patch) => m.setDetails({ ...m.details, ...patch })}
                      highlighted={m.highlightNonceFor(DETAILS) !== null}
                    />
                  ) : m.selectedSection && m.selectedCategory ? (
                    <SectionCard
                      section={m.selectedSection}
                      category={m.selectedCategory}
                      onAddField={(atStart) => m.addField(m.selectedSection!.uid, atStart)}
                      onUpdateField={(fieldUid, patch) =>
                        m.updateField(m.selectedSection!.uid, fieldUid, patch)
                      }
                      onMoveField={(fieldIndex, delta) =>
                        m.moveField(m.selectedSection!.uid, fieldIndex, delta)
                      }
                      onReorderField={(from, to) =>
                        m.reorderField(m.selectedSection!.uid, from, to)
                      }
                      onRemoveField={(fieldUid) =>
                        m.removeField(m.selectedSection!.uid, fieldUid)
                      }
                      highlightNonceFor={m.highlightNonceFor}
                    />
                  ) : (
                    <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border p-6 text-center">
                      <ListChecks className="size-7 text-muted-foreground/40" />
                      <p className="m-0 text-[12.5px] text-muted-foreground">
                        Add a category and a section from the sidebar to start adding fields
                      </p>
                    </div>
                  )}

                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
